import { useContent, usePageLoading } from '@wix/funnel-builder-lizard'
import { Button, TextButton } from '@wix/wildcat-ui'
import { ArrowRight } from '@wix/wix-ui-icons-common'
import classNames from 'classnames'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { usePageLogger } from '../../../bi/mfi'
import { useNext } from '../../../hooks/useNext'
import { usePrevious } from '../../../utils/usePrevious'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { RichText } from '../../reusable/RichText'
import Tick from './tick.svg'
import { TWelcomeV2 as Content } from './WelcomeV2.content'
import { welcomeV2DataHooks as dataHooks } from './WelcomeV2.dataHooks'
import s from './WelcomeV2.scss'

type ElementType<T> = T extends (infer U)[] ? U : never
type Option = ElementType<Content['question']['options']>

export const WelcomeV2: FC = () => {
  const pageLogger = usePageLogger()
  const next = useNext()
  const { content } = useContent<Content>()
  const { skip, next: nextBtn, question, legalDisclaimer } = content
  const { questionKey, content: questionContent, options: questionOptions } = question
  const [searchParams] = useSearchParams()
  const flowContext = searchParams.get('flowContext')
  const prevFlowContext = usePrevious(flowContext)
  const [pageLoading] = usePageLoading()
  const precheckedOption = useMemo(() => {
    return content.question.options.find(option => option.isChecked)
  }, [content])

  const [clickedButton, setClickedButton] = useState<'next' | 'skip'>()

  const [selectedOption, setSelectedOption] = useState<Option | undefined>(precheckedOption)

  const handleChange = (value?: number | string) => {
    const option = questionOptions.find(option => option.optionKey === value)
    if (!option) {
      return
    }
    void pageLogger.clickAnswer({ questionKey, buttonName: String(value) })
    setSelectedOption(option)
  }

  const handleSkip = () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('skip')
    void pageLogger.generalAction({ action: 'skip' })
    next({
      [questionKey]: {
        skipped: true,
      },
    })
  }

  const handleNext = async () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('next')
    void pageLogger.answer({ answer: selectedOption?.optionKey || '' })
    next({
      [questionKey]: { values: [selectedOption?.optionKey ?? ''] },
    })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    if (prevFlowContext !== flowContext && !pageLoading) {
      setClickedButton(undefined)
      setSelectedOption(precheckedOption)
    }
  }, [clickedButton, flowContext, prevFlowContext, pageLoading, precheckedOption])

  return (
    <div className={s.container}>
      <WixLogo className={s.logo} />
      <div className={s.cardGroup}>
        <div className={s.leftCard}>
          <h2 className={s.subtitle} data-hook={dataHooks.subtitle()}>
            {questionContent.subtitle}
          </h2>
          <h1 className={s.title} data-hook={dataHooks.title()}>
            {questionContent.title}
          </h1>
          <div className={s.radioGroup}>
            {questionOptions.map(option => (
              <label
                key={option.optionKey}
                className={classNames(s.radioOption, {
                  [s.radioOptionSelected]: selectedOption?.optionKey === option.optionKey,
                })}
                data-hook={dataHooks.radio({ optionKey: option.optionKey })}
              >
                <input
                  type="radio"
                  name={questionKey}
                  value={option.optionKey}
                  checked={selectedOption?.optionKey === option.optionKey}
                  onChange={() => handleChange(option.optionKey)}
                  className={s.radioInput}
                />
                <span className={s.radioIndicator}>{selectedOption?.optionKey === option.optionKey && <Tick />}</span>
                <span className={s.radioLabel}>{option.title}</span>
              </label>
            ))}
          </div>
          <Button
            isLoading={clickedButton === 'next'}
            onClick={handleNext}
            disabled={selectedOption === undefined}
            className={s.button}
            size="large"
            dataHook={dataHooks.next()}
          >
            {nextBtn}
          </Button>
          {legalDisclaimer && (
            <RichText light secondary size="small" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
              {legalDisclaimer}
            </RichText>
          )}
        </div>
        <div className={s.videoContainer}>
          <video className={s.video} src={questionContent.src} loop autoPlay muted />
        </div>
      </div>
      <TextButton onClick={handleSkip} className={s.skipButton} suffixIcon={<ArrowRight />} dataHook={dataHooks.skip()}>
        {skip}
      </TextButton>
    </div>
  )
}
